import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COLLECTIONS,
  collectionColumns,
  getCollection,
  type CollectionGroup,
  type OrgMember,
  type OrgRecord,
  type Paginated,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { RecordWizard } from './RecordWizard.js';
import { AssignmentsDialog } from './AssignmentsDialog.js';
import { formatFieldValue } from './format.js';
import './records.css';

const MAX_COLUMNS = 6;

/** Records referenced by `record` fields of the returned rows (list/detail `refs`). */
type RecordRefs = Record<string, { name: string; collection: string }>;

// ------------------------------------------------------ classification guide

const GUIDE_STORAGE_KEY = 'za.records.classification-guide';

function readGuideOpen(): boolean {
  try {
    return localStorage.getItem(GUIDE_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

const CLASSIFICATION_GUIDE: ReadonlyArray<{ term: string; meaning: string }> = [
  {
    term: 'Public',
    meaning:
      'Approved for unlimited public release; unauthorised disclosure would have a limited adverse impact.',
  },
  {
    term: 'Internal use',
    meaning: 'For employees only; disclosure could have a serious adverse impact.',
  },
  {
    term: 'Confidential',
    meaning:
      'For designated individuals or groups only; disclosure could have a critical impact on operations, assets, reputation or customers.',
  },
  {
    term: 'Strictly confidential',
    meaning:
      'The most private or sensitive information — financial and business plans, proprietary technical data, shareholder information, source code.',
  },
  {
    term: 'Integrity',
    meaning:
      'Low / Moderate / High = limited / serious / critical impact if the information were modified or destroyed without authorisation.',
  },
  {
    term: 'Availability',
    meaning:
      'Low / Moderate / High = limited / serious / critical impact of losing access, with an initial response within 3 business days / 1 business day / 4 business hours.',
  },
  {
    term: 'N/A',
    meaning: 'The asset holds no information of its own (e.g. monitors, keys, browsers).',
  },
];

/** Collapsible legend for the Information Assets classification columns (collapse state in localStorage). */
function ClassificationGuide() {
  const [open, setOpen] = useState(readGuideOpen);
  const toggle = () =>
    setOpen((current) => {
      try {
        localStorage.setItem(GUIDE_STORAGE_KEY, current ? '0' : '1');
      } catch {
        // Storage unavailable (private mode) — state still toggles for this view.
      }
      return !current;
    });
  return (
    <section className="classification-guide" aria-labelledby="classification-guide-title">
      <button
        type="button"
        className="classification-guide-toggle"
        aria-expanded={open}
        aria-controls="classification-guide-body"
        onClick={toggle}
      >
        <span id="classification-guide-title" className="classification-guide-title">
          Classification guide
        </span>
        <span className="classification-guide-chevron" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>
      <div id="classification-guide-body" className="classification-guide-body" hidden={!open}>
        <dl>
          {CLASSIFICATION_GUIDE.map((entry) => (
            <div key={entry.term} className="classification-guide-row">
              <dt>{entry.term}</dt>
              <dd>{entry.meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------- page

/** Generic list page for one inventory/registry collection (/assets/:collection, /registers/:collection). */
export function RecordsPage({ group }: { group: CollectionGroup }) {
  const { collection = '' } = useParams();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  const isAdmin = org.role === 'admin';

  const def = getCollection(collection);

  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const records = useQuery({
    queryKey: ['records', org.orgId, collection, status],
    queryFn: () =>
      apiFetch<Paginated<OrgRecord> & { refs: RecordRefs }>(`/orgs/${org.orgId}/records`, {
        query: { collection, status: status || undefined, limit: 200 },
      }),
    enabled: !!def,
  });
  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
  });

  const [wizard, setWizard] = useState<{ open: boolean; record: OrgRecord | null }>({
    open: false,
    record: null,
  });
  const [assignFor, setAssignFor] = useState<OrgRecord | null>(null);
  const [deleteFor, setDeleteFor] = useState<OrgRecord | null>(null);

  // Deep link (?record=<id>, e.g. "Open DPIA" from the coverage page): writers
  // land in the edit wizard, readers see the row highlighted under "All".
  const [params, setParams] = useSearchParams();
  const focusId = params.get('record');
  const focused = useQuery({
    queryKey: ['record', org.orgId, focusId],
    queryFn: () =>
      apiFetch<OrgRecord & { refs: RecordRefs }>(`/orgs/${org.orgId}/records/${focusId}`),
    enabled: !!focusId && !!def,
    retry: false,
  });
  const handledFocus = useRef<string | null>(null);
  const scrolledFocus = useRef<string | null>(null);
  const clearFocus = () => {
    handledFocus.current = null;
    scrolledFocus.current = null;
    setParams({}, { replace: true });
  };
  useEffect(() => {
    if (!focusId || !def || handledFocus.current === focusId) return;
    if (focused.isError) {
      handledFocus.current = focusId;
      toast('That record could not be found.', 'error');
      clearFocus();
      return;
    }
    const record = focused.data;
    if (!record) return;
    handledFocus.current = focusId;
    if (record.collection !== def.key) {
      toast(
        `That record is filed under ${getCollection(record.collection)?.name ?? 'another collection'}.`,
        'error',
      );
      clearFocus();
      return;
    }
    if (canWrite) setWizard({ open: true, record });
    else setStatus('');
    // `toast` and `clearFocus` are not tracked: they never change what this
    // effect decides, and `handledFocus` guards against a second run per id.
  }, [focusId, focused.data, focused.isError, def?.key, canWrite]);
  useEffect(() => {
    if (!focusId || canWrite || scrolledFocus.current === focusId) return;
    const row = document.getElementById(`record-${focusId}`);
    if (!row) return;
    scrolledFocus.current = focusId;
    row.scrollIntoView({ block: 'center' });
  }, [focusId, canWrite, records.data]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['records', org.orgId] });
  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');

  const setRecordStatus = useMutation({
    mutationFn: ({ record, next }: { record: OrgRecord; next: 'active' | 'archived' }) =>
      apiFetch<OrgRecord>(`/orgs/${org.orgId}/records/${record.id}`, {
        method: 'PATCH',
        body: { status: next },
      }),
    onSuccess: (updated, vars) => {
      toast(
        vars.next === 'archived'
          ? 'Record archived.'
          : vars.record.status === 'draft'
            ? `“${updated.name}” approved into the inventory.`
            : 'Record restored.',
        'success',
      );
      invalidate();
    },
    onError,
  });

  const move = useMutation({
    mutationFn: ({ record, collection }: { record: OrgRecord; collection: string }) =>
      apiFetch<OrgRecord>(`/orgs/${org.orgId}/records/${record.id}`, {
        method: 'PATCH',
        body: { collection },
      }),
    onSuccess: (updated) => {
      toast(
        `“${updated.name}” moved to ${getCollection(updated.collection)?.name ?? updated.collection}.`,
        'success',
      );
      invalidate();
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (record: OrgRecord) =>
      apiFetch<{ ok: true }>(`/orgs/${org.orgId}/records/${record.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteFor(null);
      toast('Record deleted.', 'success');
      invalidate();
    },
    onError,
  });

  const memberNames = useMemo(
    () => new Map((members.data ?? []).map((m) => [m.user.id, m.user.name])),
    [members.data],
  );
  const recordNames = useMemo(
    () => new Map(Object.entries(records.data?.refs ?? {}).map(([id, ref]) => [id, ref.name])),
    [records.data],
  );

  const filtered = useMemo(() => {
    const items = records.data?.items ?? [];
    if (!search.trim()) return items;
    const needle = search.toLowerCase();
    return items.filter((r) => r.name.toLowerCase().includes(needle));
  }, [records.data, search]);

  if (!def || def.group !== group) {
    return <EmptyState title="Unknown collection" message="This section does not exist." />;
  }
  if (records.isLoading) return <LoadingPane label={`Loading ${def.name.toLowerCase()}`} />;

  const columns = collectionColumns(def).slice(0, MAX_COLUMNS);

  return (
    <div>
      <PageHeader
        title={def.name}
        subtitle={def.description}
        actions={
          canWrite && (
            <Button onClick={() => setWizard({ open: true, record: null })}>
              Add {def.singular}
            </Button>
          )
        }
      />

      <div className="filter-bar" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Input
          aria-label={`Search ${def.name}`}
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ width: 'auto' }}
        >
          <option value="active">Active</option>
          <option value="draft">Draft imports</option>
          <option value="archived">Archived</option>
          <option value="">All</option>
        </Select>
      </div>

      {def.key === 'information-assets' && <ClassificationGuide />}

      {filtered.length === 0 ? (
        <EmptyState
          title={`No ${def.name.toLowerCase()} yet`}
          message={
            canWrite
              ? `Add your first ${def.singular} — assessments can then import this list as evidence.`
              : 'Nothing has been recorded yet.'
          }
          action={
            canWrite && (
              <Button onClick={() => setWizard({ open: true, record: null })}>
                Add {def.singular}
              </Button>
            )
          }
        />
      ) : (
        <Table
          head={
            <>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              <th>Status</th>
              <th>Updated</th>
              {(canWrite || isAdmin) && <th />}
            </>
          }
        >
          {filtered.map((record) => (
            <tr
              key={record.id}
              id={`record-${record.id}`}
              className={focusId === record.id ? 'record-row-focus' : undefined}
              aria-current={focusId === record.id ? 'true' : undefined}
            >
              {columns.map((c, i) => (
                <td key={c.key} style={i === 0 ? { fontWeight: 600 } : undefined}>
                  {formatFieldValue(c, record.data[c.key], memberNames, recordNames)}
                </td>
              ))}
              <td>
                <Badge
                  tone={
                    record.status === 'active'
                      ? 'success'
                      : record.status === 'draft'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {record.status}
                </Badge>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(record.updatedAt)}</td>
              {(canWrite || isAdmin) && (
                <td>
                  <div className="record-row-actions">
                    {canWrite && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setWizard({ open: true, record })}
                      >
                        Edit
                      </Button>
                    )}
                    {def.assignable && (
                      <Button variant="secondary" size="sm" onClick={() => setAssignFor(record)}>
                        Assignments
                      </Button>
                    )}
                    {canWrite && (
                      <Button
                        variant={record.status === 'draft' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() =>
                          setRecordStatus.mutate({
                            record,
                            next: record.status === 'active' ? 'archived' : 'active',
                          })
                        }
                      >
                        {record.status === 'active'
                          ? 'Archive'
                          : record.status === 'draft'
                            ? 'Approve'
                            : 'Restore'}
                      </Button>
                    )}
                    {canWrite && record.status === 'draft' && (
                      <Select
                        aria-label={`Move ${record.name} to another collection`}
                        value=""
                        style={{ width: 'auto' }}
                        onChange={(e) => {
                          if (e.target.value) move.mutate({ record, collection: e.target.value });
                        }}
                      >
                        <option value="">Move to…</option>
                        {COLLECTIONS.filter((d) => d.group === group && d.key !== def.key).map(
                          (d) => (
                            <option key={d.key} value={d.key}>
                              {d.name}
                            </option>
                          ),
                        )}
                      </Select>
                    )}
                    {(isAdmin || (canWrite && record.status === 'draft')) && (
                      <Button variant="danger" size="sm" onClick={() => setDeleteFor(record)}>
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </Table>
      )}

      <RecordWizard
        def={def}
        record={wizard.record}
        open={wizard.open}
        onClose={() => {
          setWizard({ open: false, record: null });
          if (focusId) clearFocus();
        }}
      />

      {assignFor && (
        <AssignmentsDialog
          def={def}
          record={assignFor}
          open={!!assignFor}
          onClose={() => setAssignFor(null)}
        />
      )}

      <Dialog open={!!deleteFor} onClose={() => setDeleteFor(null)} title={`Delete ${def.singular}`}>
        <p>
          Delete “{deleteFor?.name}” permanently? Its assignment history goes with it. Archiving is
          usually the better option — deleted records disappear from future snapshots and reports.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setDeleteFor(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => deleteFor && remove.mutate(deleteFor)}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
